/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  ArrowLeft,
  CreditCard,
  Tag,
  ShieldCheck,
  Gift,
  Percent,
  X,
  ChevronRight,
  Package,
  Sparkles,
  Zap,
  Lock,
  Heart,
  TrendingUp
} from 'lucide-react';
import { formatINR } from '../utils/currency';
import { useCart } from '../context/CartContext';
import { API_URL } from '../api';

/* ============================================================
   ANIMATION VARIANTS
   ============================================================ */
const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.4 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.35 } }
};

const slideOut = {
  hidden: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -100, transition: { duration: 0.3 } }
};

/* ============================================================
   HELPERS
   ============================================================ */
const BACKEND_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5001';

const getImageUrl = (imagePath: string): string => {
  if (!imagePath) return '';
  if (imagePath.startsWith('http')) return imagePath;
  return `${BACKEND_URL}${imagePath}`;
};

interface CartItemType {
  _id: string;
  productId: string;
  name: string;
  images: { url: string; alt: string }[];
  price: {
    selling: number;
    mrp: number;
    currency: string;
  };
  quantity: number;
  variant?: {
    color?: string;
    size?: string;
  };
  brand?: string;
}

const AVAILABLE_PROMOS = [
  { code: 'SAVE10', discount: 0.10, description: '10% off on your order' },
  { code: 'FIRST50', discount: 50, description: '₹50 off on first order', type: 'fixed' },
  { code: 'FREESHIP', discount: 0, description: 'Free shipping', type: 'shipping' },
];

/* ============================================================
   HERO SECTION
   ============================================================ */
const HeroSection = ({ itemCount }: { itemCount: number }) => (
  <section className="relative overflow-hidden bg-gray-950 py-10 sm:py-14">
    {/* Animated gradient orbs */}
    <div className="absolute top-0 right-0 w-72 h-72 bg-purple-600/20 rounded-full blur-[100px] animate-pulse" />
    <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-[90px]" />
    <div className="absolute top-1/2 left-1/2 w-48 h-48 bg-indigo-500/10 rounded-full blur-[80px]" />

    <div className="container mx-auto px-4 relative z-10">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
        className="max-w-4xl mx-auto text-center"
      >
        {/* Breadcrumb */}
        <motion.div variants={fadeInUp} className="flex items-center justify-center gap-2 text-sm text-white/50 mb-5">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-white font-medium">Shopping Cart</span>
        </motion.div>

        {/* Title */}
        <motion.h1 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3">
          Your Shopping Cart
        </motion.h1>
        <motion.p variants={fadeInUp} className="text-base sm:text-lg text-white/60 max-w-lg mx-auto">
          {itemCount} {itemCount === 1 ? 'item' : 'items'} ready for checkout
        </motion.p>
      </motion.div>
    </div>
  </section>
);

/* ============================================================
   CART ITEM CARD
   ============================================================ */
const CartItemCard = ({
  item,
  onUpdateQuantity,
  onRemove,
  index
}: {
  item: CartItemType;
  onUpdateQuantity: (item: CartItemType, newQty: number) => void;
  onRemove: (item: CartItemType) => void;
  index: number;
}) => {
  const savings = item.price.mrp > item.price.selling
    ? item.price.mrp - item.price.selling
    : 0;
  const discountPercent = item.price.mrp > item.price.selling
    ? Math.round((1 - item.price.selling / item.price.mrp) * 100)
    : 0;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -80, transition: { duration: 0.25 } }}
      transition={{ duration: 0.35, delay: index * 0.06 }}
      className="group relative bg-white/80 backdrop-blur-sm border border-white/60 rounded-2xl p-4 sm:p-5 hover:shadow-lg hover:bg-white transition-all duration-300"
    >
      <div className="flex gap-4">
        {/* Product Image */}
        <div className="relative flex-shrink-0">
          <Link to={`/product/${item.productId}`}>
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden bg-gray-50 border border-gray-100 group-hover:border-gray-200 transition-colors">
              <img
                src={getImageUrl(item.images?.[0]?.url || '/placeholder-product.jpg')}
                alt={item.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
          </Link>
          {discountPercent > 0 && (
            <span className="absolute -top-2 -left-2 bg-gradient-to-r from-red-500 to-rose-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
              {discountPercent}% OFF
            </span>
          )}
        </div>

        {/* Product Info */}
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex justify-between items-start gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                {item.brand || 'NLS Store'}
              </p>
              <Link to={`/product/${item.productId}`}>
                <h3 className="font-semibold text-gray-900 text-sm sm:text-base leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                  {item.name}
                </h3>
              </Link>
              {item.variant && (item.variant.color || item.variant.size) && (
                <p className="text-xs text-gray-500 mt-1">
                  {item.variant.color && <span className="inline-flex items-center gap-1 bg-gray-100 rounded-md px-2 py-0.5 mr-2">
                    <span className="w-2 h-2 rounded-full bg-gray-400" />{item.variant.color}
                  </span>}
                  {item.variant.size && <span className="inline-flex items-center bg-gray-100 rounded-md px-2 py-0.5">
                    Size: {item.variant.size}
                  </span>}
                </p>
              )}
            </div>

            {/* Mobile Remove */}
            <button
              onClick={() => onRemove(item)}
              className="sm:hidden p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
              title="Remove"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Price & Controls Row */}
          <div className="flex items-end justify-between mt-auto pt-3">
            {/* Quantity */}
            <div className="flex items-center bg-gray-100/80 rounded-xl border border-gray-200/60">
              <button
                onClick={() => onUpdateQuantity(item, item.quantity - 1)}
                disabled={item.quantity <= 1}
                className="p-2.5 hover:bg-white rounded-l-xl transition-colors disabled:opacity-30"
              >
                <Minus className="w-3.5 h-3.5 text-gray-700" />
              </button>
              <span className="px-3 py-2 text-sm font-bold text-gray-900 min-w-[40px] text-center tabular-nums">
                {item.quantity}
              </span>
              <button
                onClick={() => onUpdateQuantity(item, item.quantity + 1)}
                className="p-2.5 hover:bg-white rounded-r-xl transition-colors"
              >
                <Plus className="w-3.5 h-3.5 text-gray-700" />
              </button>
            </div>

            {/* Price */}
            <div className="text-right">
              <p className="text-lg font-bold text-gray-900 tabular-nums">
                {formatINR(item.price.selling * item.quantity)}
              </p>
              {savings > 0 && (
                <p className="text-xs text-gray-400 line-through tabular-nums">
                  {formatINR(item.price.mrp * item.quantity)}
                </p>
              )}
            </div>

            {/* Desktop Remove */}
            <button
              onClick={() => onRemove(item)}
              className="hidden sm:flex p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all ml-2"
              title="Remove"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/* ============================================================
   ORDER SUMMARY
   ============================================================ */
const OrderSummary = ({
  cartItems,
  appliedPromo,
  promoCode,
  promoError,
  showPromoList,
  onPromoChange,
  onApplyPromo,
  onRemovePromo,
  onTogglePromoList,
  onApplySuggested,
  onCheckout
}: {
  cartItems: CartItemType[];
  appliedPromo: { code: string; discount: number; type?: string } | null;
  promoCode: string;
  promoError: string;
  showPromoList: boolean;
  onPromoChange: (v: string) => void;
  onApplyPromo: () => void;
  onRemovePromo: () => void;
  onTogglePromoList: () => void;
  onApplySuggested: (code: string) => void;
  onCheckout: () => void;
}) => {
  const subtotal = cartItems.reduce((t, i) => t + i.price.selling * i.quantity, 0);
  const mrpTotal = cartItems.reduce((t, i) => t + i.price.mrp * i.quantity, 0);
  const savings = mrpTotal - subtotal;

  const discount = appliedPromo
    ? appliedPromo.type === 'fixed'
      ? appliedPromo.discount
      : subtotal * appliedPromo.discount
    : 0;
  const total = subtotal - discount;
  const itemCount = cartItems.reduce((c, i) => c + i.quantity, 0);

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="lg:sticky lg:top-28 space-y-4"
    >
      {/* Summary Card */}
      <div className="bg-white/80 backdrop-blur-sm border border-white/60 rounded-2xl overflow-hidden shadow-sm">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 px-5 py-4">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-white/80" />
            <h2 className="text-base font-bold text-white">Order Summary</h2>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Price Breakdown */}
          <div className="space-y-3 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'})</span>
              <span className="font-medium text-gray-900 tabular-nums">{formatINR(subtotal)}</span>
            </div>

            {savings > 0 && !appliedPromo && (
              <div className="flex justify-between text-emerald-600">
                <span className="flex items-center gap-1">
                  <TrendingUp className="w-3.5 h-3.5" />You Save
                </span>
                <span className="font-medium tabular-nums">-{formatINR(savings)}</span>
              </div>
            )}

            {appliedPromo && (
              <div className="flex justify-between text-emerald-600 bg-emerald-50/70 rounded-lg px-3 py-2">
                <span className="flex items-center gap-1">
                  <Zap className="w-3.5 h-3.5" />Promo {appliedPromo.code}
                </span>
                <span className="font-medium tabular-nums">-{formatINR(discount)}</span>
              </div>
            )}
          </div>

          {/* Total */}
          <div className="border-t border-gray-100 pt-4">
            <div className="flex justify-between items-center">
              <span className="text-base font-bold text-gray-900">Total</span>
              <span className="text-2xl font-extrabold text-gray-900 tabular-nums">{formatINR(total)}</span>
            </div>
          </div>

          {/* Promo Code */}
          <div className="border-t border-gray-100 pt-4">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Apply Coupon
            </label>

            {appliedPromo ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-800 text-sm">{appliedPromo.code}</p>
                    <p className="text-xs text-emerald-600">Applied!</p>
                  </div>
                </div>
                <button
                  onClick={onRemovePromo}
                  className="p-1.5 text-emerald-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => onPromoChange(e.target.value.toUpperCase())}
                    placeholder="e.g. SAVE10"
                    className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400 transition-all placeholder:text-gray-400"
                  />
                  <button
                    onClick={onApplyPromo}
                    disabled={!promoCode.trim()}
                    className="px-4 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed font-semibold text-sm transition-all"
                  >
                    Apply
                  </button>
                </div>
                {promoError && (
                  <p className="text-red-500 text-xs mt-2">{promoError}</p>
                )}
                <button
                  onClick={onTogglePromoList}
                  className="mt-3 text-xs font-medium text-gray-500 hover:text-gray-900 flex items-center gap-1 transition-colors"
                >
                  <Gift className="w-3.5 h-3.5" />
                  {showPromoList ? 'Hide offers' : 'View available offers'}
                </button>
                <AnimatePresence>
                  {showPromoList && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-2 space-y-2">
                        {AVAILABLE_PROMOS.map((promo) => (
                          <button
                            key={promo.code}
                            onClick={() => onApplySuggested(promo.code)}
                            className="w-full text-left bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 hover:bg-gray-100 hover:border-gray-300 transition-all group"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Tag className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600" />
                                <span className="font-bold text-gray-700 text-sm">{promo.code}</span>
                              </div>
                              <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500" />
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5 ml-5.5">{promo.description}</p>
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            )}
          </div>

          {/* Checkout Button */}
          <button
            onClick={onCheckout}
            className="w-full bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 hover:from-black hover:to-gray-800 text-white font-bold py-3.5 rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 text-sm"
          >
            <CreditCard className="w-4 h-4" />
            <span>Proceed to Checkout</span>
            <ChevronRight className="w-4 h-4" />
          </button>

          {/* Security */}
          <div className="flex items-center justify-center gap-2 text-xs text-gray-400 pt-1">
            <Lock className="w-3 h-3 text-emerald-500" />
            <span>Secure SSL encrypted checkout</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/* ============================================================
   EMPTY STATE
   ============================================================ */
const EmptyState = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gray-50">
      <HeroSection itemCount={0} />

      <div className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-md mx-auto text-center"
        >
          <div className="relative w-40 h-40 mx-auto mb-8">
            <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full animate-pulse" />
            <div className="absolute inset-2 bg-white rounded-full flex items-center justify-center">
              <ShoppingCart className="w-16 h-16 text-gray-300" />
            </div>
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="absolute -top-2 -right-2 w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-400 rounded-full flex items-center justify-center shadow-lg"
            >
              <Sparkles className="w-5 h-5 text-white" />
            </motion.div>
          </div>

          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
            Your cart is empty
          </h2>
          <p className="text-gray-500 mb-8 max-w-sm mx-auto">
            Looks like you haven't added anything yet. Explore our collection and find something you'll love.
          </p>

          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-gray-900 text-white rounded-xl font-semibold hover:bg-black transition-all shadow-lg hover:shadow-xl"
          >
            <Package className="w-5 h-5" />
            Start Shopping
          </button>
        </motion.div>
      </div>
    </div>
  );
};

/* ============================================================
   MAIN PAGE
   ============================================================ */
const CartPage = () => {
  const { cartItems: contextCartItems, removeFromCart, updateQuantity } = useCart();
  const navigate = useNavigate();
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState<{ code: string; discount: number; type?: string } | null>(null);
  const [promoError, setPromoError] = useState('');
  const [showPromoList, setShowPromoList] = useState(false);

  const cartItems: CartItemType[] = contextCartItems.map((item, index) => ({
    _id: `${item.productId}-${index}`,
    productId: item.productId,
    name: item.name,
    images: [{ url: item.image, alt: item.name }],
    price: { selling: item.price, mrp: item.mrp, currency: 'INR' },
    quantity: item.quantity,
    variant: item.variant
  }));

  const handleUpdateQuantity = (item: CartItemType, newQuantity: number) => {
    updateQuantity(item.productId, newQuantity, item.variant);
  };

  const handleRemoveItem = (item: CartItemType) => {
    removeFromCart(item.productId, item.variant);
  };

  const calculateSubtotal = () =>
    cartItems.reduce((t, i) => t + i.price.selling * i.quantity, 0);

  const calculateDiscount = () => {
    if (!appliedPromo) return 0;
    const subtotal = calculateSubtotal();
    if (appliedPromo.type === 'fixed') return appliedPromo.discount;
    return subtotal * appliedPromo.discount;
  };

  const calculateTotal = () => calculateSubtotal() - calculateDiscount();

  const applyPromoCode = async () => {
    setPromoError('');
    const hardcoded = AVAILABLE_PROMOS.find(p => p.code === promoCode.toUpperCase());
    if (hardcoded) {
      setAppliedPromo({ code: hardcoded.code, discount: hardcoded.discount, type: hardcoded.type });
      setPromoCode('');
      setShowPromoList(false);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: promoCode, totalAmount: calculateSubtotal() })
      });
      const data = await res.json();
      if (data.success) {
        const c = data.data.coupon;
        setAppliedPromo({
          code: c.code,
          discount: c.discountPercentage > 0 ? c.discountPercentage / 100 : c.discountAmount,
          type: c.discountPercentage > 0 ? 'percentage' : 'fixed'
        });
        setPromoCode('');
        setShowPromoList(false);
      } else {
        setPromoError(data.message || 'Invalid coupon');
      }
    } catch {
      setPromoError('Failed to validate coupon');
    }
  };

  const removePromoCode = () => {
    setAppliedPromo(null);
    setPromoError('');
  };

  const applySuggestedPromo = (code: string) => {
    const promo = AVAILABLE_PROMOS.find(p => p.code === code);
    if (promo) {
      setAppliedPromo({ code: promo.code, discount: promo.discount, type: promo.type });
      setPromoCode('');
      setShowPromoList(false);
    }
  };

  const handleCheckout = () => {
    navigate('/checkout', {
      state: {
        isBuyNow: false,
        cartItems: contextCartItems.map((item, index) => ({
          _id: `${item.productId}-${index}`,
          productId: item.productId,
          name: item.name,
          price: { selling: item.price, mrp: item.mrp },
          images: [{ url: item.image }],
          quantity: item.quantity,
          image: item.image,
          variant: item.variant
        }))
      }
    });
  };

  const itemCount = cartItems.reduce((c, i) => c + i.quantity, 0);

  if (cartItems.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <HeroSection itemCount={itemCount} />

      <div className="container mx-auto px-4 py-8 lg:py-12">
        {/* Back Link */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-6"
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Continue Shopping
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            <AnimatePresence>
              {cartItems.map((item, index) => (
                <CartItemCard
                  key={item._id}
                  item={item}
                  index={index}
                  onUpdateQuantity={handleUpdateQuantity}
                  onRemove={handleRemoveItem}
                />
              ))}
            </AnimatePresence>

            {/* Bottom CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-6 text-white text-center"
            >
              <Heart className="w-8 h-8 mx-auto mb-3 text-red-400" />
              <h3 className="text-lg font-bold mb-1">Need more items?</h3>
              <p className="text-white/60 text-sm mb-4">Explore our latest collection before checkout</p>
              <Link
                to="/products"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl text-sm font-semibold transition-all"
              >
                Browse Products <ChevronRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <OrderSummary
              cartItems={cartItems}
              appliedPromo={appliedPromo}
              promoCode={promoCode}
              promoError={promoError}
              showPromoList={showPromoList}
              onPromoChange={setPromoCode}
              onApplyPromo={applyPromoCode}
              onRemovePromo={removePromoCode}
              onTogglePromoList={() => setShowPromoList(!showPromoList)}
              onApplySuggested={applySuggestedPromo}
              onCheckout={handleCheckout}
            />
          </div>
        </div>
      </div>

      {/* Mobile Fixed Bottom Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-gray-200 p-4 z-50 safe-area-pb">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500">Total</p>
            <p className="text-xl font-extrabold text-gray-900 tabular-nums">{formatINR(calculateTotal())}</p>
          </div>
          <button
            onClick={handleCheckout}
            className="bg-gray-900 text-white font-bold px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm"
          >
            Checkout <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
